import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Pagination, PermissionAssigneeTargetLinkContainerModel, PermissionSetService, TenantPermissionAssigneeTargetService } from 'blg-akaun-ts-lib';
import { fetchAllByCriteria } from '../../../../listing.utils';
import { ToastrService } from 'ngx-toastr';
import { AppConfig } from '../../../../visa';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, takeLast } from 'rxjs/operators';
import { PermSetActions } from '../actions';

@Injectable()
export class PermSetEffects {

    private readonly apiVisa = AppConfig.apiVisa;

    /**
     * Create new permission set.
     */
    createPermSet$ = createEffect(() => this.actions$.pipe(
        ofType(PermSetActions.createPermSetInit),
        mergeMap(action => this.permSetService.post(action.permSet, this.apiVisa)
        .pipe(
            map(_ => {
                this.displayToasterSuccess('Permission set created successfully');
                return PermSetActions.createPermSetSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermSetActions.createPermSetFailure({error: err.message}))
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermSetActions.createPermSetFailure({error: err.message}))
        })
    ))

    /**
     * Update permission set.
     */
    updatePermSet$ = createEffect(() => this.actions$.pipe(
        ofType(PermSetActions.updatePermSetInit),
        mergeMap(action => this.permSetService.put(action.permSet, this.apiVisa)
        .pipe(
            map(_ => {
                this.displayToasterSuccess('Permission set updated successfully');
                return PermSetActions.updatePermSetSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermSetActions.updatePermSetFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermSetActions.updatePermSetFailure({error: err.message}));
        })
    ));

    /**
     * Update perm assignee target links with new target hdr guids.
     */
    updatePermSetAssigneeTargetLinks$ = createEffect(() => this.actions$.pipe(
        ofType(PermSetActions.updatePermAssigneeTargetLinksInit),
        concatMap(action => action.lines),
        concatMap(line => {
            const permSetHdrGuid = line.hdr_guid;
            const permSetLineGuid = line.guid;

            const criteria = [
                {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
                {columnName: 'perm_set_hdr_guid', operator: '=', value: permSetHdrGuid.toString()},
                {columnName: 'perm_set_line_guid', operator: '=', value: permSetLineGuid.toString()}
            ]

            const pagination = new Pagination(null, null, criteria);

            return this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa)
            .pipe(
                concatMap(x => x.data),
                mergeMap((x: PermissionAssigneeTargetLinkContainerModel) => {
                    // update the target hdr guid
                    x.permAssigneeTargetLink.target_hdr_guid = line.target_hdr_guid;

                    // send put request to the new container
                    return this.tenantPermAssigneeTargetLinkService.put(x, this.apiVisa)
                    .pipe(
                        catchError((err) => {
                            this.displayToasterError(err.message);
                            return of(PermSetActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
                        })
                    )
                }),
                catchError((err) => {
                    this.displayToasterError(err.message);
                    return of(PermSetActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
                })
            )
        }),
        map((_) => {
            this.displayToasterSuccess('Permission assignee target links updated successfully');
            return PermSetActions.updatePermAssigneeTargetLinksSuccess();
        }),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermSetActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
        })
    ))

    /**
     * Delete permission set.
     */
    deletePermSet$ = createEffect(() => this.actions$.pipe(
        ofType(PermSetActions.deletePermSetInit),
        mergeMap(action => this.permSetService.delete(action.guid, this.apiVisa)
        .pipe(
            map(() => {
                this.displayToasterSuccess('Permission set deleted successfully');
                return PermSetActions.deletePermSetSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermSetActions.deletePermSetFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermSetActions.deletePermSetFailure({error: err.message}));
        })
    ))

    /**
     * Delete permission assignee target links associated with this
     * permission set guid.
     */
    deletePermAssigneeTargetLinks$ = createEffect(() => this.actions$.pipe(
        ofType(PermSetActions.deletePermAssigneeTargetLinksInit),
        /**
         * Resolve the links to delete.
         *
         * Replaces the raw SELECT that went through SubQueryService:
         *
         *   SELECT DISTINCT(hdr.guid) FROM app_perm_assignee_target_link hdr
         *   WHERE hdr.perm_set_hdr_guid = '<guid>' AND hdr.status != 'DELETED'
         *
         * GET permission-assignee-target/query ->
         * PermissionAssigneeTargetLinkUow.getByCriteria ->
         * generateWhereClauseByCriteria honours `perm_set_hdr_guid` and defaults to
         * `status != 'DELETED'` when no status is sent, so both halves of the WHERE
         * clause are expressed as criteria. DISTINCT is redundant here: the old
         * query selected the primary key.
         *
         * fetchAllByCriteria pages until the endpoint stops returning full pages,
         * so every matching link is deleted. A single getByCriteria would cap the
         * deletion at one page and leave residual links behind, which the raw
         * subquery never did.
         */
        concatMap(action => fetchAllByCriteria<PermissionAssigneeTargetLinkContainerModel>(
            pagination => this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa),
            [{ columnName: 'perm_set_hdr_guid', operator: '=', value: action.permSetHdrGuid }]
        )),
        map(links => links.map(link => link.permAssigneeTargetLink.guid.toString())),
        filter(res => res.length > 0),
        // return guids
        concatMap(res => res),
        mergeMap(guid => this.tenantPermAssigneeTargetLinkService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Permission assignee target links deleted successfully');
                return PermSetActions.deletePermAssigneeTargetLinksSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermSetActions.deletePermAssigneeTargetLinksFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermSetActions.deletePermAssigneeTargetLinksFailure({error: err.message}));
        })
    ))

    constructor(
        private tenantPermAssigneeTargetLinkService: TenantPermissionAssigneeTargetService,
        private permSetService: PermissionSetService,
        private actions$: Actions,
        private toaster: ToastrService
    ) {};

    /**
     * Display a successful toaster message.
     *
     * @param message : the message to display
     * @returns the toaster
     */
    displayToasterSuccess(message: string) {
        return this.toaster.success(
            message,
            'Success',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

    /**
     * Display a toaster when there is an error.
     *
     * @param message : the messsage to display
     * @returns the toaster
     */
    displayToasterError(message: string) {
        return this.toaster.error(
            message,
            'Error',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

}
